/**
 * Vaqtinchalik parol bilan kirgan xodim (owner/menejer bergan, email yo'q -- 2026-10-18) platformaga kirishdan oldin
 * o'z parolini qo'yadi. Backend ham shu vaqtgacha API'ni yopiq tutadi (accounts/middleware.py,
 * `password_change_required`); joriy parol so'ralmaydi -- u hozirgina shu parol bilan kirdi.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { meKey, useLogout } from "@/features/auth/hooks";
import type { Me } from "@/features/auth/types";
import { startLoginIntro } from "@/features/auth/intro";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { AuthLayout } from "./AuthLayout";

export function SetPasswordPage() {
  const { t } = useTranslation("auth");
  const qc = useQueryClient();
  const logout = useLogout();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rules = [
    { ok: p1.length >= 10, label: t("setPassword.ruleLength") },
    { ok: p1 !== "" && p1 === p2, label: t("setPassword.ruleMatch") },
  ];
  const valid = rules.every((r) => r.ok);

  const save = useMutation({
    mutationFn: () => api.post("/api/auth/password/change/", { new_password: p1 }),
    onSuccess: async () => {
      // Endi platforma ochiq: "me" yangilanadi (must_change_password = false), brend intro va platforma
      const me = await api.get<Me>("/api/auth/me/");
      startLoginIntro();
      qc.setQueryData(meKey, me);
    },
    onError: (err) => setError(err instanceof ApiError ? (err.fields?.new_password?.join(" ") ?? err.message) : t("setPassword.failed")),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    save.mutate();
  }

  return (
    <AuthLayout
      title={t("setPassword.title")}
      subtitle={t("setPassword.subtitle")}
      footer={
        <button type="button" className="text-primary hover:underline" onClick={() => logout.mutate()}>
          {t("twofaSetup.signOut")}
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
            {error}
          </div>
        )}
        <Field label={t("setPassword.newPassword")} htmlFor="p1">
          <Input id="p1" type="password" autoComplete="new-password" autoFocus value={p1} onChange={(e) => setP1(e.target.value)} />
        </Field>
        <Field label={t("setPassword.confirm")} htmlFor="p2">
          <Input id="p2" type="password" autoComplete="new-password" value={p2} onChange={(e) => setP2(e.target.value)} />
        </Field>
        <ul className="space-y-0.5 text-[11px]">
          {rules.map((r) => (
            <li key={r.label} className={r.ok ? "text-success" : "text-muted"}>
              {r.ok ? "✓" : "○"} {r.label}
            </li>
          ))}
          <li className="text-muted">○ {t("setPassword.ruleDifferent")}</li>
        </ul>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={save.isPending} disabled={!valid}>
          {t("setPassword.save")}
        </Button>
      </form>
    </AuthLayout>
  );
}
